import { useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useUser } from "../../../api/useAuth";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardDateFilter } from "../../../dashboard-framework/DashboardDateFilter";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { formatDecimal, formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { DrillDownConfig, GlobalFilterValues } from "../../../dashboard-framework/types";
import { DataTableCard } from "../../../dashboard-framework/widgets/DataTableCard";
import { sessionsDrillColumns, SESSIONS_DRILL_FIELDS, sessionRoute } from "../shared/drillColumns";
import {
    describeNeurofeedbackTablePt,
    formatHours,
    FUNCTION_OPTIONS,
    NfbFunctionFilterKey,
    NFB_PERIOD_PRESETS,
    technicianDoneSessionsDrillFilter,
} from "../shared/neurofeedbackProductivityConfig";
import { NeurofeedbackRow, neurofeedbackTotals } from "../shared/neurofeedbackProductivityRows";
import { useNeurofeedbackProductivityRows } from "../shared/useNeurofeedbackProductivityRows";

const tableColumns = [
    { key: "person", label: "Técnico" },
    { key: "horario", label: "Horário lançado", isNumeric: true },
    { key: "efetivo", label: "Tempo em sessão", isNumeric: true },
    { key: "reunioes", label: "Reuniões / Formações", isNumeric: true },
    { key: "morto", label: "Tempo sem sessão", isNumeric: true },
    { key: "mortoPercent", label: "% Tempo sem sessão", isNumeric: true },
    { key: "mortoPerDay", label: "Tempo médio sem sessão por dia", isNumeric: true, width: 150 },
    { key: "brainMaps", label: "Brain maps", isNumeric: true },
    { key: "ratio", label: "Brain maps por hora", isNumeric: true },
];

const buildDisplayRows = (tableRows: Array<NeurofeedbackRow>) => {
    const displayRows = tableRows.map((row) => {
        const displayRow: Record<string, React.ReactNode> = {
            person: (
                <Typography variant="body2" fontWeight={600}>
                    {row.displayName}
                </Typography>
            ),
            horario: formatHours(row.horarioHours),
            efetivo: formatHours(row.efetivoHours),
            reunioes: formatHours(row.reunioesHours),
            morto: (
                <Typography variant="body2" component="span" sx={{ color: "#993c1d" }}>
                    {formatHours(row.mortoHours)}
                </Typography>
            ),
            mortoPercent: (
                <Typography variant="body2" component="span" fontWeight={700} sx={{ color: "#993c1d" }}>
                    {row.mortoPercent === null ? "—" : formatPercent(row.mortoPercent, 1)}
                </Typography>
            ),
            mortoPerDay: (
                <Typography variant="body2" component="span" sx={{ color: "#993c1d" }}>
                    {row.mortoPerWorkedDayHours === null ? "—" : formatHours(row.mortoPerWorkedDayHours)}
                </Typography>
            ),
            brainMaps: (
                <Typography variant="body2" component="span" fontWeight={600}>
                    {formatQuantity(row.brainMaps)}
                </Typography>
            ),
            ratio: (
                <Typography variant="body2" component="span" fontWeight={600}>
                    {row.ratio === null ? "—" : formatDecimal(row.ratio, 1)}
                </Typography>
            ),
        };
        return displayRow;
    });
    return displayRows;
};

const buildTotalsRow = (tableRows: Array<NeurofeedbackRow>) => {
    const totals = neurofeedbackTotals(tableRows);
    const totalsRow = {
        person: `Total (${tableRows.length} ${tableRows.length === 1 ? "técnico" : "técnicos"})`,
        horario: formatHours(totals.horarioHours),
        efetivo: formatHours(totals.efetivoHours),
        reunioes: formatHours(totals.reunioesHours),
        morto: formatHours(totals.mortoHours),
        mortoPercent: totals.mortoPercent === null ? "—" : formatPercent(totals.mortoPercent, 1),
        mortoPerDay: totals.mortoPerWorkedDayHours === null ? "—" : formatHours(totals.mortoPerWorkedDayHours),
        brainMaps: formatQuantity(totals.brainMaps),
        ratio: totals.ratio === null ? "—" : formatDecimal(totals.ratio, 1),
    };
    return totalsRow;
};

export const NeurofeedbackProductivityDashboard = () => {
    const access = useDashboardAccess();
    const userResult = useUser();
    const [periodValues, setPeriodValues] = useState<GlobalFilterValues>({ period: "ultimos_30_dias_ate_ontem" });
    const setPeriodValue = (filterKey: string, value: string | number | null) => {
        setPeriodValues((currentValues) => ({ ...currentValues, [filterKey]: value }));
    };

    // Vista pessoal (viewer_rules → params.technicianUserId): recorta tudo ao próprio.
    // Enquanto o $current_user não resolve, as queries ficam parqueadas (nunca mostrar tudo).
    const currentUserId: string | null = userResult.data?.id || null;
    const viewerParamValue = access.viewerParams?.technicianUserId;
    const technicianUserId = viewerParamValue === undefined ? null : viewerParamValue === "$current_user" ? currentUserId : String(viewerParamValue);
    const isOwnView = viewerParamValue !== undefined;
    const viewerPending = isOwnView && technicianUserId === null;
    // Recorte ao utilizador só na vista pessoal; na vista completa não há restrição (todos entram).
    const restrictToUserId = technicianUserId;

    const now = new Date();
    // periodRange (exato) recorta client-side e descreve; fetchRange (alargado ±1 dia) alimenta
    // as queries e o drill — contrato de dois ranges do recorte por dia de Lisboa.
    const periodRange = resolveRequiredDateFilterRange(periodValues, "period", "ultimos_30_dias_ate_ontem", now);
    const productivity = useNeurofeedbackProductivityRows({ periodRange, restrictToUserId, enabled: !viewerPending });
    const { rows, fetchRange, reachedPageLimit, isError } = productivity;
    const isLoading = viewerPending || productivity.isLoading;

    // Uma tabela por função (só as funções com técnicos com atividade). Cada técnico entra numa só
    // tabela — a sua função primária (Neurofeedback > Psicologia > Apoio ao cliente > Outros); as
    // métricas da linha contam toda a atividade da pessoa (qualquer serviço), a tabela é só o grupo.
    const functionGroups = FUNCTION_OPTIONS.map((option) => ({
        key: option.key,
        label: option.label,
        rows: rows.filter((row) => row.functionKey === option.key),
    })).filter((group) => group.rows.length > 0);

    // Drill da linha: as sessões de cliente realizadas que originam o Tempo em sessão do técnico
    // (mesmo filtro do cartão, capturado no render). Gate por drill_roles via DrillDownProvider.
    const makeRowDrillDown = (tableRows: Array<NeurofeedbackRow>) => (rowIndex: number): DrillDownConfig | null => {
        const row = tableRows[rowIndex];
        if (!row) return null;
        const config: DrillDownConfig = {
            kind: "query",
            title: `Sessões realizadas — ${row.displayName}`,
            entity: "sessions",
            fieldsQuery: SESSIONS_DRILL_FIELDS,
            filter: technicianDoneSessionsDrillFilter(fetchRange, row.userId),
            defaultSort: "-start_date",
            columns: sessionsDrillColumns,
            rowRoute: sessionRoute,
            queryDescription: `Sessões de cliente realizadas (qualquer serviço) de ${row.displayName} que somam o Tempo em sessão na vista atual.`,
        };
        return config;
    };

    const renderProductivityTable = (key: string, title: string, tableRows: Array<NeurofeedbackRow>, functionScope: NfbFunctionFilterKey) => {
        const card = (
            <DataTableCard
                key={key}
                title={title}
                explanation={{ queryDescription: describeNeurofeedbackTablePt(periodRange, isOwnView, functionScope) }}
                columns={tableColumns}
                rows={buildDisplayRows(tableRows)}
                totalsRow={buildTotalsRow(tableRows)}
                maxBodyHeight={560}
                rowDrillDown={makeRowDrillDown(tableRows)}
                isLoading={isLoading}
                isError={isError}
            />
        );
        return card;
    };

    return (
        <DashboardPage globalFilters={[]} values={{}} onChange={() => undefined}>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
                <DashboardDateFilter label="Período" filterKey="period" values={periodValues} onChange={setPeriodValue} presets={NFB_PERIOD_PRESETS} />
            </Box>

            {reachedPageLimit && (
                <Alert severity="warning">
                    O período escolhido devolve demasiados registos e os números podem estar incompletos — encurta o período.
                </Alert>
            )}

            {isOwnView
                ? renderProductivityTable("own", "A tua produtividade", rows, "todas")
                : isLoading || functionGroups.length === 0
                  ? renderProductivityTable("all", "Produtividade por técnico", rows, "todas")
                  : functionGroups.map((group) => renderProductivityTable(group.key, group.label, group.rows, group.key))}
        </DashboardPage>
    );
};
